import type {ReactElement} from 'react';
import React from 'react';
import {getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import {ContextualMenuMediaControlOptions} from '@Components/contextual-menu/components/contextual-menu-media-control-options';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {ReplaceMediaControls} from '@Components/replace-media-controls';
import {onOpenReplaceBackground, onOpenTrimVideoModal, onOpenVideoBackgroundCropModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {VideoBgBlackAndWhiteOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-black-and-white-option';
import {VideoBgSepiaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-sepia-option';
import {VideoBgInvertOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-invert-option';
import {VideoBgBrightnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-brightness-option';
import {VideoBgContrastOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-contrast-option';
import {VideoBgVibranceOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-vibrance-option';
import {VideoBgSaturationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-saturation-option';
import {VideoBgTintOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-tint-option';
import {VideoBgMultiplyOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-multiply-option';
import {VideoBgGammaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-gamma-option';
import {VideoBgGenerateAiSubtitlesOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-generate-ai-subtitles-option';
import {VideoBgVolumeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-volume-option';
import {VideoBgSpeedOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-speed-option';
import {PosterEditorDesignSubPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-default-options/poster-editing-default-options.types';
import {isVideoBackground, BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';
import {useAppSelector} from '@/hooks';

interface VideoBackgroundOptionsProps {
  onClickColorFill(option: PosterEditorDesignSubPanels): void;
}

export function VideoBackgroundOptions({...props}: VideoBackgroundOptionsProps): ReactElement<any> | null {
  const currentPageBackground = useAppSelector(getCurrentPageBackground);

  if (!currentPageBackground) {
    return null;
  }

  if (currentPageBackground.details.type !== BackgroundTypeName.VIDEO) {
    return null;
  }

  const onCropVideo = (): void => {
    onOpenVideoBackgroundCropModal();
  };

  const onTrimVideo = (): void => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster) {
      return;
    }
    const backgroundItem = poster.getCurrentPage().background.details;
    if (!isVideoBackground(backgroundItem)) {
      return;
    }
    onOpenTrimVideoModal(backgroundItem);
  };

  const getItems = (): MediaControlsItemProps[] => {
    return [
      {
        id: 'background-video-crop',
        icon: 'icon-crop',
        text: window.i18next.t('pmwjs_crop'),
        onClick: onCropVideo,
      },
      {
        id: 'background-video-replace',
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
        dropdownAlignment: ALIGNMENT_TYPE.BOTTOM,
        dropdownContent: (
          <ReplaceMediaControls
            onClick={(id: ReplaceMediaControlsButtons): void => {
              onOpenReplaceBackground(id);
            }}
          />
        ),
      },
      {
        id: 'background-video-trim',
        icon: 'icon-cut',
        text: window.i18next.t('pmwjs_trim'),
        onClick: onTrimVideo,
      },
    ];
  };

  return (
    <>
      <ContextualMenuMediaControlOptions id="background-video-media-controls" items={getItems()} columns={3} />
      <VideoBgGenerateAiSubtitlesOption />
      <VideoBgVolumeOption />
      <VideoBgSpeedOption />
      <ContextualMenuHeadingOption id="video-background-effects-heading" text={window.i18next.t('pmwjs_effects')} />
      <VideoBgBlackAndWhiteOption />
      <VideoBgSepiaOption />
      <VideoBgInvertOption />
      <ContextualMenuHeadingOption id="video-background-adjustments-heading" text={window.i18next.t('pmwjs_adjustments')} />
      <VideoBgBrightnessOption />
      <VideoBgContrastOption />
      <VideoBgVibranceOption />
      <VideoBgSaturationOption />
      <VideoBgTintOption
        onClickColorFill={(): void => {
          props.onClickColorFill(PosterEditorDesignSubPanels.BACKGROUND_VIDEO_TINT_COLOR);
        }}
      />
      <VideoBgMultiplyOption
        onClickColorFill={(): void => {
          props.onClickColorFill(PosterEditorDesignSubPanels.BACKGROUND_VIDEO_MULTIPLY_COLOR);
        }}
      />
      <VideoBgGammaOption />
    </>
  );
}
